// frontend/src/pages/DashboardPage.js
import React, { useState, useEffect, useCallback } from "react"; 
import { Row, Col, Modal, ModalHeader, ModalBody, Input } from "reactstrap"; 
import SeriesGrid from "../components/SeriesGrid";
import MoviesGrid from "../components/MoviesGrid";
// import Widgets from "../components/Widgets"; // Removed in refactor
import ModalBasic from "../components/ModalBasic";

import SeriesForm from "../components/SeriesForm";
 
import api from "../api/api";
import Swal from "sweetalert2";
import MainLayout from "../components/layouts/MainLayout"; 
import "../assets/scss/DashboardPage.scss";

const DashboardPage = () => {


  const [seriesData, setSeriesData] = useState([]);
  const [totalSeries, setTotalSeries] = useState(0);
  const [, setLoadingSeries] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [seriesPerPage] = useState(12); 
  const [filterActive, setFilterActive] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [showAddSerieModal, setShowAddSerieModal] = useState(false);
  const [isSubmittingSerie, setIsSubmittingSerie] = useState(false);



  const [showEditSerieModal, setShowEditSerieModal] = useState(false);
  const [selectedSerieToEdit, setSelectedSerieToEdit] = useState(null);
  const [isUpdatingSerie, setIsUpdatingSerie] = useState(false);

  const [selectedGenres, setSelectedGenres] = useState([]);
  const [availableGenres, setAvailableGenres] = useState([]);

  // --- Search & Filters Movies ---
  const [movieSearchTerm, setMovieSearchTerm] = useState("");
  const [availableMovieGenres, setAvailableMovieGenres] = useState([]);
  const [selectedMovieGenres, setSelectedMovieGenres] = useState([]); 

  // --- Video Modal State ---
  const [showVideosModal, setShowVideosModal] = useState(false);
  const [videoList, setVideoList] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null); // If playing a specific video
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [selectedMediaTitle, setSelectedMediaTitle] = useState(""); 

  // --- Fetch Data ---
  const [moviesData, setMoviesData] = useState([]);
  const [, setLoadingMovies] = useState(true);
  // Derived state for filtered movies (calculated in render or memo)
  // We will do it in render for simplicity unless perf issues arise


  // --- Fetch Data ---
  const fetchMovies = useCallback(async () => {
    setLoadingMovies(true);
    try {
        const response = await api.get("/movies");
        if (response.data.operation === "SUCCESS") {
            const movies = response.data.movies || [];
            setMoviesData(movies);

            // Extract Movie Genres
            const genres = new Set();
            movies.forEach(m => {
                if (m.genero) genres.add(m.genero);
            });
            setAvailableMovieGenres(Array.from(genres).sort());
        }
    } catch (error) {
        console.error("Error fetching movies:", error);
    } finally {
        setLoadingMovies(false);
    }
  }, []);

  const handleDeleteMovie = async (id) => {
    const result = await Swal.fire({
      title: 'Eliminar pel·lícula?',
      text: "S'eliminarà de la teua llista.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancel·lar',
      reverseButtons: true
    });
    
    if (!result.isConfirmed) return;
    
    try {
      await api.delete(`/movies/${id}`);
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Pel·lícula eliminada',
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true
      });
      fetchMovies();
    } catch (error) {
       Swal.fire("Error", "No s'ha pogut eliminar la pel·lícula.", "error");
    }
 };

  const fetchSeries = useCallback(async () => {
    setLoadingSeries(true);
    try {
      // Production API compatibility: It seems /series/actives does not exist on remote.
      // We will fetch all from /series and filter, OR use query params if supported.
      // Falling back to robust /series endpoint and client-side filtering logic for safety.
      const endpoint = "/series"; 
      
      const response = await api.get(endpoint);
      if (response.status === 200) { // Relaxed check
        // FALLBACK: Try to find the array
        let allSeries = response.data.data || response.data || []; 
        if (!Array.isArray(allSeries) && response.data.series) allSeries = response.data.series;

        // 1. First, separate by Active/Inactive status
        if (filterActive) {
            allSeries = allSeries.filter(s => {
                const isActive = s.activa === true || s.status === 'active' || s.active === true;
                return isActive;
            });
        } else {
            allSeries = allSeries.filter(s => {
                 const isInactive = s.activa === false || (s.activa !== true && s.status !== 'active' && s.active !== true);
                 return isInactive;
            });
        }

        // 2. Extract Genres dynamically from current view (before search/pagination)
        const genresSet = new Set();
        allSeries.forEach(s => {
            if (s.genero) genresSet.add(s.genero);
        });
        setAvailableGenres(Array.from(genresSet).sort());

        // 3. Client-side Search Filtering
        if (searchTerm) {
          const lowerTerm = searchTerm.toLowerCase();
          allSeries = allSeries.filter((s) =>
            s.nom.toLowerCase().includes(lowerTerm)
          );
        }

        // 4. Genre Filtering
        if (selectedGenres.length > 0) {
            allSeries = allSeries.filter(s => s.genero && selectedGenres.includes(s.genero));
        }

        setTotalSeries(allSeries.length);

        // Client-side Pagination
        const indexOfLastSerie = currentPage * seriesPerPage;
        const indexOfFirstSerie = indexOfLastSerie - seriesPerPage;
        const currentSeries = allSeries.slice(
          indexOfFirstSerie,
          indexOfLastSerie
        );

        setSeriesData(currentSeries);
      } else {
        console.error("Error fetching series:", response.data.message);
        setSeriesData([]); 
      }
    } catch (error) {
       console.error("Error fetching series (network):", error);
    } finally {
      setLoadingSeries(false);
    }
  }, [filterActive, currentPage, seriesPerPage, searchTerm, selectedGenres]);

  useEffect(() => {
    fetchSeries();
    fetchMovies();
  }, [fetchSeries, fetchMovies]);

  // Reset page and selected genres when switching active/inactive tabs
  const handleTabChange = (active) => {
      setFilterActive(active);
      setCurrentPage(1);
      setSelectedGenres([]);
  };

  // --- Handlers ---
  const handleActivate = async (id) => {
    try {
      await api.put(`/series/${id}/activate`); // ✅ Endpoint correcto
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'Sèrie reactivada',
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true
      });
      fetchSeries();
    } catch (error) {
      Swal.fire("Error", "No s'ha pogut activar la sèrie.", "error");
    }
  };

  const handleDeactivate = async (id) => {
    const result = await Swal.fire({
      title: 'Pausar sèrie?',
      text: "La sèrie es mourà a inactives. Pots reactivar-la quan vulgues.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ffc107',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, pausar',
      cancelButtonText: 'Cancel·lar',
      reverseButtons: true
    });
    
    if (!result.isConfirmed) return;
    
    try {
       await api.put(`/series/${id}/deactivate`); // ✅ Endpoint correcto para desactivar
       Swal.fire({
         toast: true,
         position: 'top',
         icon: 'success',
         title: 'Sèrie pausada correctament',
         showConfirmButton: false,
         timer: 2500,
         timerProgressBar: true
       });
       fetchSeries();
    } catch (error) {
      Swal.fire("Error", "No s'ha pogut pausar la sèrie.", "error");
    }
  };

  const handleDelete = async (id) => {
     const result = await Swal.fire({
       title: 'Eliminar sèrie?',
       html: "<p>Esta acció <strong>eliminarà permanentment</strong> la sèrie.</p><p class='text-danger mb-0'>⚠️ No es pot desfer</p>",
       icon: 'warning',
       showCancelButton: true,
       confirmButtonColor: '#dc3545',
       cancelButtonColor: '#6c757d',
       confirmButtonText: 'Sí, eliminar',
       cancelButtonText: 'Cancel·lar',
       reverseButtons: true
     });
     
     if (!result.isConfirmed) return;
     
     try {
       await api.delete(`/series/${id}`); // ✅ Endpoint correcto para eliminar
       Swal.fire({
         toast: true,
         position: 'top',
         icon: 'success',
         title: 'Sèrie eliminada',
         showConfirmButton: false,
         timer: 2500,
         timerProgressBar: true
       });
       fetchSeries();
     } catch (error) {
        Swal.fire("Error", "No s'ha pogut eliminar la sèrie.", "error");
     }
  };

  const handleEdit = (serie) => {
    setSelectedSerieToEdit(serie);
    setShowEditSerieModal(true);
  };

  const handleIncrement = async (serie) => {
    try {
      const currentCapitolNumber = Number(serie.capitol) || 0;
      const total = Number(serie.capitolsTemporada) || 0;
      const season = Number(serie.temporada) || 1;
      
      // Extraer el número de episodio (últimos 2 dígitos)
      const currentEpisode = currentCapitolNumber % 100;
      
      // Check if season is complete
      if (total > 0 && currentEpisode >= total) {
          const result = await Swal.fire({
              title: '🎉 Temporada completada!',
              html: `<p>Ja has vist tots els <strong>${total} capítols</strong> d'esta temporada.</p><p>Vols incrementar igualment el comptador?</p>`,
              icon: 'success',
              showCancelButton: true,
              confirmButtonColor: '#10b981',
              cancelButtonColor: '#6c757d',
              confirmButtonText: 'Sí, incrementar',
              cancelButtonText: 'No, cancel·lar',
              reverseButtons: true
          });
          if (!result.isConfirmed) return;
      }

      // Incrementar episodio y reconstruir número completo
      const newEpisode = currentEpisode + 1;
      const newCapitol = season * 100 + newEpisode;
      
      // Show loading toast
      Swal.fire({
          title: 'Actualitzant progrés...',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
          didOpen: (toast) => {
              Swal.showLoading();
          }
      });

      await api.put(`/series/${serie._id}`, { capitol: newCapitol });
      
      // Success toast
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: `Capítol ${newEpisode} marcat`,
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true
      });
      
      fetchSeries();
    } catch (error) {
      Swal.fire("Error", "No s'ha pogut actualitzar el progrés.", "error");
    }
  };

  const handleAutoDownload = async (serie) => {
    try {
      // Buscar el episodio actual marcado en la BBDD
      const currentCapitolNumber = Number(serie.capitol) || 0;
      const season = Number(serie.temporada) || 1;
      const currentEpisode = currentCapitolNumber % 100;
      
      // Construir query en formato BTDigg: NOMBRE+[RESOLUCION][Cap.NUMERO]
      // Ejemplo: Fallout+[HDTV 720p][Cap.201]
      const cleanDefinicio = serie.definicio.replace(/[[\]]/g, '');
      const query = `${serie.nom}+[${cleanDefinicio}][Cap.${currentCapitolNumber}]`;
      
      // Mostrar loading
      Swal.fire({
        title: 'Buscant torrents...',
        html: `<p>Buscant: <strong>${query}</strong></p><p class="text-muted small">Açò pot tardar uns segons...</p>`,
        toast: false,
        position: 'center',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      // Importar dinámicamente el scraper
      const { searchBTDiggClient } = await import('../services/btdigg-client-scraper');
      
      // Realizar scraping desde el navegador
      let results;
      try {
        results = await searchBTDiggClient(query);
      } catch (error) {
        console.error('Error en scraping:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error de cerca',
          html: `<p>No s'ha pogut accedir a BTDigg.</p><p class="text-muted small">${error.message}</p>`,
          confirmButtonText: 'Entés'
        });
        return;
      }
      
      if (!results || results.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Sense resultats',
          text: 'No s\'han trobat torrents per a esta cerca.',
          confirmButtonText: 'Entés'
        });
        return;
      }
      
      // Enviar resultados al backend para procesamiento
      try {
        const response = await api.post('/torrents/client-search', {
          results: results,
          serieName: serie.nom,
          resolution: cleanDefinicio,
          episode: currentEpisode,
          season: season
        });
        
        const { operation, message, torrent, closestMatch } = response.data;
        
        if (operation === 'SUCCESS') {
          // Calcular el siguiente episodio para mostrar
          const nextCapitol = currentCapitolNumber + 1;
          const nextSeason = Math.floor(nextCapitol / 100);
          const nextEp = nextCapitol % 100;
          
          Swal.fire({
            icon: 'success',
            title: '🎉 Descàrrega iniciada!',
            html: `
              <div style="text-align: left; padding: 10px;">
                <p style="margin-bottom: 15px;"><strong>📺 ${serie.nom}</strong></p>
                
                <div style="background: #f0f9ff; padding: 12px; border-radius: 8px; margin-bottom: 15px;">
                  <p style="margin: 5px 0; font-size: 14px;"><strong>🎬 Torrent:</strong></p>
                  <p style="margin: 5px 0; font-size: 13px; color: #555;">${torrent.title}</p>
                </div>
                
                <div style="display: flex; gap: 15px; margin-bottom: 15px;">
                  <div style="flex: 1; background: #f0fdf4; padding: 10px; border-radius: 6px;">
                    <p style="margin: 0; font-size: 12px; color: #666;">Tamany</p>
                    <p style="margin: 5px 0 0 0; font-size: 14px; font-weight: bold; color: #059669;">${torrent.size}</p>
                  </div>
                  <div style="flex: 1; background: #fef3c7; padding: 10px; border-radius: 6px;">
                    <p style="margin: 0; font-size: 12px; color: #666;">Arxius</p>
                    <p style="margin: 5px 0 0 0; font-size: 14px; font-weight: bold; color: #d97706;">${torrent.files}</p>
                  </div>
                </div>
                
                <div style="background: #ede9fe; padding: 12px; border-radius: 8px; border-left: 4px solid #7c3aed;">
                  <p style="margin: 0; font-size: 13px;">
                    ✅ Comptador actualitzat a <strong>T${nextSeason} Cap.${nextEp}</strong>
                  </p>
                </div>
              </div>
            `,
            width: '500px',
            showConfirmButton: true,
            confirmButtonText: 'Genial! 🚀',
            confirmButtonColor: '#10b981'
          });
          
          // Incrementar automáticamente el contador SIN mostrar toast adicional
          try {
            const newCapitol = currentCapitolNumber + 1;
            await api.put(`/series/${serie._id}`, { capitol: newCapitol });
            fetchSeries(); // Refrescar la lista
          } catch (error) {
            console.error('Error actualizando contador:', error);
          }
          
        } else if (operation === 'NO_EXACT_MATCH') {
          Swal.fire({
            icon: 'warning',
            title: 'Sense coincidència exacta',
            html: `
              <p>${message}</p>
              ${closestMatch ? `
                <hr>
                <p class="text-muted small mb-1"><strong>Resultat més proper:</strong></p>
                <p class="small mb-1">${closestMatch.title}</p>
                <p class="text-muted small mb-0">Tamany: ${closestMatch.size} | Arxius: ${closestMatch.files}</p>
              ` : ''}
            `,
            confirmButtonText: 'Entés'
          });
        } else {
          Swal.fire({
            icon: 'info',
            title: 'Sense resultats',
            text: message || 'No s\'ha trobat cap coincidència vàlida.',
            confirmButtonText: 'Entés'
          });
        }
        
      } catch (error) {
        console.error('Error enviando al backend:', error);
        
        let errorMessage = 'Error al procesar la descarga.';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonText: 'Entés'
        });
      }
      
    } catch (error) {
      console.error('Error general en handleAutoDownload:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error inesperat',
        text: 'Ha ocorregut un error en intentar descarregar el torrent.',
        confirmButtonText: 'Entés'
      });
    }
  };

  const handleUpdateSerie = async (updatedData) => {
      if (!selectedSerieToEdit) return;
      setIsUpdatingSerie(true);
      try {
          await api.put(`/series/${selectedSerieToEdit._id}`, updatedData);
          setShowEditSerieModal(false);
          Swal.fire({
            toast: true,
            position: 'top',
            icon: 'success',
            title: 'Sèrie actualitzada correctament',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true
          });
          fetchSeries(); // Refresh list
      } catch (error) {
          console.error("Error updating series:", error);
          Swal.fire("Error", "No s'ha pogut actualitzar la sèrie.", "error");
      } finally {
          setIsUpdatingSerie(false);
      }
  };

  const handleOpenVideos = async (media) => {

      
      const tmdbId = media.tmdb_id || (media.movieInfo ? media.movieInfo.tmdb_id : null);
      
      if (!tmdbId) {
          console.warn("No TMDB ID found for:", media);
          Swal.fire({
              title: 'Sense informació',
              text: 'Aquesta fitxa no té identificador TMDB associat.',
              icon: 'info'
          });
          return;
      }


      setLoadingVideos(true);
      setSelectedMediaTitle(media.nom || media.name);
      setShowVideosModal(true);
      setVideoList([]);
      setCurrentVideo(null);

      try {
          const type = media.type || 'movie'; 
          const response = await api.get(`/tmdb/${tmdbId}/videos`, { params: { type } });
          
          if (response.data.operation === "SUCCESS") {
              const videos = response.data.videos || [];

              
              if (videos.length > 0) {
                  setVideoList(videos);
                  setLoadingVideos(false); // Enable Modal Content
                  // Modal is already open, so it will render the list
              } else {
                  // NO OFFICIAL VIDEOS -> FALLBACK TO SEARCH

                  setShowVideosModal(false); // Close the modal
                  
                  // Construct standard search query
                  const query = `${media.nom || media.name || ''} trailer español`;
                  const searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
                  window.open(searchUrl, '_blank');
                  
                  // Optional: Notify user
                  Swal.fire({
                      toast: true,
                      position: 'top-end',
                      icon: 'info',
                      title: 'Videos oficials no disponibles. Cercant a YouTube...',
                      showConfirmButton: false,
                      timer: 3000
                  });
              }
          }
      } catch (error) {
          console.error("Error fetching videos:", error);
          // Fallback on error too? Or just alert?
          // Let's fallback on error too for better UX
          setShowVideosModal(false);
          const query = `${media.nom || media.name || ''} trailer español`;
          window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`, '_blank');
      } finally {
          setLoadingVideos(false);
      }
  };

  const handleAddSerie = async (serieData) => {
     setIsSubmittingSerie(true);
     try {
        const payload = {
            name: serieData.nom, 
            imatge: serieData.image, 
            temporada: parseInt(serieData.temporada, 10),
            capitol: parseInt(serieData.capitol, 10),
            definicio: serieData.definicio,
            capitolsTemporada: parseInt(serieData.capitolsTemporada, 10),
            activa: true,
            revisada: false,
        };

        await api.post("/series", payload);
        setShowAddSerieModal(false);
        Swal.fire({
          toast: true,
          position: 'top',
          icon: 'success',
          title: 'Nova sèrie afegida',
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true
        });
        fetchSeries();
     } catch (error) {
        Swal.fire("Error", "Error en afegir la sèrie", "error");
     } finally {
        setIsSubmittingSerie(false);
     }
  };




  // --- Render ---
  return (
    <MainLayout>
      <div className="dashboard-container">
        {/* Search and Filters Header - Two Row Layout */}
        <div className="d-flex flex-column mb-5 gap-3">
          {/* Title Section - Row 1 */}
          <div className="d-flex flex-column text-start w-100 border-bottom pb-3">
             <h2 className="fw-bold text-dark mb-1">
                {filterActive ? "Sèries Actives" : "Arxiu de Sèries"}
             </h2>
             <p className="text-muted small mb-0">Gestiona el teu progrés y descàrregues.</p>
          </div>
          
          {/* Controls Container - Row 2 - Centered Widget (Shrink Wrapped) */}
          <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 w-auto mx-auto ms-md-auto me-md-auto" style={{ maxWidth: '100%' }}>
             {/* Premium Search - Expanded Width */}
             <div className="search-box-premium" style={{ width: '350px', flexShrink: 0 }}>
                 <i className="bi bi-search"></i>
                 <Input 
                    type="text" 
                    placeholder="Buscar sèrie..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-control"
                    style={{ paddingLeft: '2.5rem', width: '350px', maxWidth: '100%' }} 
                 />
             </div>

             {/* Premium Filter Toolbar - Inline & Centered */}
             {availableGenres.length > 0 && (
                 <div 
                    className="d-flex align-items-center bg-white border px-3 gap-2"
                    style={{ 
                        height: '54px', 
                        borderRadius: '27px', 
                        maxWidth: '100%', 
                        minWidth: '0', 
                        width: 'auto',
                        flexShrink: 1,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                    }}
                 >
                    {/* Icon */}
                    <div className="text-muted d-flex align-items-center pe-3 border-end flex-shrink-0">
                        <i className="bi bi-funnel-fill text-muted" style={{ fontSize: '1.1rem' }}></i>
                    </div>

                    {/* Scrollable Tags Area */}
                    <div 
                        className="d-flex align-items-center gap-2 py-2" 
                        style={{ 
                            flex: 1,
                            minWidth: 0,
                            overflowX: 'auto', 
                            whiteSpace: 'nowrap', 
                            scrollbarWidth: 'none', 
                            msOverflowStyle: 'none', 
                            WebkitOverflowScrolling: 'touch', // Critical for iOS smooth scroll
                            maskImage: 'linear-gradient(to right, black 90%, transparent 100%)', 
                            WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent 100%)'
                        }}
                    >
                        <style>{`
                            .no-scrollbar::-webkit-scrollbar { display: none; }
                        `}</style>
                        
                        {availableGenres.map(genre => {
                          const isSelected = selectedGenres.includes(genre);
                          return (
                              <button
                                  key={genre}
                                  onClick={() => {
                                      setSelectedGenres(prev => 
                                          prev.includes(genre) 
                                              ? prev.filter(g => g !== genre)
                                              : [...prev, genre]
                                      );
                                      setCurrentPage(1);
                                  }}
                                  className={`btn btn-sm d-flex align-items-center transition-all flex-shrink-0 ${
                                      isSelected 
                                        ? 'bg-dark text-white border-dark' 
                                        : 'bg-light text-secondary border-0 hover-bg-gray'
                                  }`}
                                  style={{ 
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      borderRadius: '50px', 
                                      padding: '0.25rem 0.8rem',
                                  }}
                              >
                                  {genre}
                              </button>
                          );
                      })}
                    </div>

                    {/* Clear Button (End) - Reserving space */}
                    <div 
                        className="ps-2 border-start h-100 d-flex align-items-center flex-shrink-0 ms-1"
                        style={{ 
                            visibility: selectedGenres.length > 0 ? 'visible' : 'hidden', 
                            pointerEvents: selectedGenres.length > 0 ? 'auto' : 'none' 
                        }}
                    >
                        <button
                            onClick={() => { setSelectedGenres([]); setCurrentPage(1); }}
                            className="btn btn-sm d-flex align-items-center justify-content-center p-0 transition-all text-muted hover-danger"
                            title="Netejar filtres"
                            style={{ 
                              marginLeft:'7px',
                                width: '28px', 
                                minWidth: '28px', 
                                height: '28px', 
                                borderRadius: '50%',
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                flexShrink: 0
                            }}
                        >
                            <i className="bi bi-x-lg" style={{ fontSize: '0.85rem', lineHeight: 1 }}></i>
                        </button>
                        {/* Hover Styling */}
                        <style>{`
                            .hover-danger:hover {
                                color: #dc3545 !important;
                                background-color: rgba(220, 53, 69, 0.1) !important;
                            }
                        `}</style>
                    </div>
                 </div>
             )}

             {/* Premium Filter Toggle + Add Button */}
              <div className="d-flex align-items-center gap-2 w-auto filters-and-add-container">
               <div className="premium-filter-group">
                 <button 
                    className={`btn-filter-toggle ${filterActive ? 'active' : ''}`}
                    onClick={() => handleTabChange(true)}
                 >
                    <span className="status-indicator active"></span>
                    Actives
                 </button>
                 <button 
                    className={`btn-filter-toggle ${!filterActive ? 'active' : ''}`}
                    onClick={() => handleTabChange(false)}
                 >
                    <span className="status-indicator inactive"></span>
                    Inactives
                 </button>
              </div>

              {/* Add Button */}
              <button 
                className="btn-premium-add-main" 
                onClick={() => setShowAddSerieModal(true)}
                title="Afegir nova sèrie"
              >
                 <i className="bi bi-plus-lg"></i>
              </button>
          </div>
        </div>

      </div></div>

       {/* Widgets Row */}
      <Row className="mb-4">
          {/* <Widgets onPremiereClick={handlePremiereClick} /> */}
      </Row>

      {/* Series List Area */}
      <Row>
        <Col className="col-12">
            <SeriesGrid
                series={seriesData}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onActivate={handleActivate}
                onDeactivate={handleDeactivate}
                onIncrement={handleIncrement}
                onAutoDownload={handleAutoDownload}
            />
            
            {totalSeries > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <span className="text-muted small">
                        {(() => {
                            const pageNum = Number(currentPage) || 1;
                            const limit = Number(seriesPerPage) || 10;
                            const total = Number(totalSeries) || 0;
                            const start = ((pageNum - 1) * limit) + 1;
                            const end = Math.min(pageNum * limit, total);
                            if (total === 0) return "No hi ha sèries per a mostrar";
                            return `Mostrant ${start} - ${end} de ${total} sèries`;
                        })()}
                    </span>
                    <div className="pagination pagination-rounded justify-content-end mb-0 gap-2">
                        <button 
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            className="btn btn-white btn-sm rounded-pill px-3 shadow-sm border-0 fw-bold"
                        >
                            <i className="bi bi-chevron-left"></i> Anterior
                        </button>
                        <button 
                            disabled={currentPage >= Math.ceil(totalSeries / seriesPerPage)}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            className="btn btn-white btn-sm rounded-pill px-3 shadow-sm border-0 fw-bold"
                        >
                            Següent <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </Col>
      </Row>

      {/* Movies Section (Only when Active tab is selected) - Re-inserted */}
      {filterActive && moviesData.length > 0 && (
        <Row className="mt-5 mb-5">
            <Col xs={12}>
                <div className="d-flex flex-column text-start w-100 border-bottom pb-3 mb-4">
                    <h2 className="fw-bold text-dark mb-1">
                        Pel·lícules
                    </h2>
                    <p className="text-muted small mb-0">Llista de seguiment.</p>
                </div>

                {/* Movie Controls */}
                <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 w-auto mx-auto ms-md-auto me-md-auto mb-4" style={{ maxWidth: '100%' }}>
                     <div className="search-box-premium" style={{ width: '350px', flexShrink: 0 }}>
                         <i className="bi bi-search"></i>
                         <Input 
                            type="text" 
                            placeholder="Buscar pel·lícula..." 
                            value={movieSearchTerm}
                            onChange={(e) => setMovieSearchTerm(e.target.value)}
                            className="form-control"
                            style={{ paddingLeft: '2.5rem', width: '350px', maxWidth: '100%' }} 
                         />
                     </div>

                     {availableMovieGenres.length > 0 && (
                        <div 
                            className="d-flex align-items-center bg-white border px-3 gap-2"
                            style={{ 
                                height: '54px', 
                                borderRadius: '27px', 
                                maxWidth: '100%', 
                                minWidth: '0', 
                                width: 'auto',
                                flexShrink: 1,
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)'
                            }}
                        >
                            <div className="text-muted d-flex align-items-center pe-3 border-end flex-shrink-0">
                                <i className="bi bi-funnel-fill text-muted" style={{ fontSize: '1.1rem' }}></i>
                            </div>
                            <div 
                                className="d-flex align-items-center gap-2 py-2" 
                                style={{ 
                                    flex: 1, 
                                    minWidth: 0, 
                                    overflowX: 'auto', 
                                    whiteSpace: 'nowrap', 
                                    scrollbarWidth: 'none', 
                                    msOverflowStyle: 'none',
                                    maskImage: 'linear-gradient(to right, black 90%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent 100%)'
                                }}
                            >
                                <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
                                {availableMovieGenres.map(genre => {
                                    const isSelected = selectedMovieGenres.includes(genre);
                                    return (
                                        <button
                                            key={genre}
                                            onClick={() => setSelectedMovieGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre])}
                                            className={`btn btn-sm d-flex align-items-center transition-all flex-shrink-0 ${isSelected ? 'bg-dark text-white border-dark' : 'bg-light text-secondary border-0 hover-bg-gray'}`}
                                            style={{ fontSize: '0.75rem', fontWeight: 500, borderRadius: '50px', padding: '0.25rem 0.8rem' }}
                                        >
                                            {genre}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Clear Button (End) - Reserving space */}
                            <div 
                                className="ps-2 border-start h-100 d-flex align-items-center flex-shrink-0 ms-1"
                                style={{ 
                                    visibility: selectedMovieGenres.length > 0 ? 'visible' : 'hidden', 
                                    pointerEvents: selectedMovieGenres.length > 0 ? 'auto' : 'none' 
                                }}
                            >
                                <button
                                    onClick={() => setSelectedMovieGenres([])}
                                    className="btn btn-sm d-flex align-items-center justify-content-center p-0 transition-all text-muted hover-danger"
                                    title="Netejar filtres"
                                    style={{ 
                                      marginLeft:'7px',
                                        width: '28px', 
                                        minWidth: '28px', 
                                        height: '28px', 
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(0,0,0,0.03)',
                                        flexShrink: 0
                                    }}
                                >
                                    <i className="bi bi-x-lg" style={{ fontSize: '0.85rem', lineHeight: 1 }}></i>
                                </button>
                                {/* Hover Styling */}
                                <style>{`
                                    .hover-danger:hover {
                                        color: #dc3545 !important;
                                        background-color: rgba(220, 53, 69, 0.1) !important;
                                    }
                                `}</style>
                            </div>
                        </div>
                     )}
                </div>
            </Col>
            <Col xs={12}>
                <MoviesGrid 
                    movies={moviesData.filter(movie => {
                        const matchesSearch = movieSearchTerm === "" || (movie.nom && movie.nom.toLowerCase().includes(movieSearchTerm.toLowerCase()));
                        const matchesGenre = selectedMovieGenres.length === 0 || (movie.genero && selectedMovieGenres.includes(movie.genero)); 
                        return matchesSearch && matchesGenre;
                    })} 
                    onDelete={handleDeleteMovie}
                    onYouTubeClick={handleOpenVideos}
                />
            </Col>
        </Row>
      )}

      {/* Modals */}
      <ModalBasic
        isOpen={showAddSerieModal}
        toggle={() => setShowAddSerieModal(!showAddSerieModal)}
        title="Afegir Nova Sèrie"
        size="lg"
      >
        <SeriesForm 
           onSubmit={handleAddSerie} 
           onCancel={() => setShowAddSerieModal(false)}
           isLoading={isSubmittingSerie}
        />
      </ModalBasic>



      {/* Edit Modal */}
      <ModalBasic
        isOpen={showEditSerieModal}
        toggle={() => setShowEditSerieModal(false)}
        title={`Editar: ${selectedSerieToEdit?.nom}`}
      >
        <SeriesForm 
            initialData={selectedSerieToEdit} 
            onSubmit={handleUpdateSerie} 
            onCancel={() => setShowEditSerieModal(false)}
            isLoading={isUpdatingSerie}
        />
      </ModalBasic>

      {/* VIDEO LIST / PLAYER MODAL */}
      <Modal 
        isOpen={showVideosModal} 
        toggle={() => {

            setShowVideosModal(false);
            setCurrentVideo(null); // Stop playback on close
        }}
        size="lg"
        centered
        // className="video-modal" // Removed to ensure no CSS conflict
      >
        <ModalHeader toggle={() => {
             if (currentVideo) {
                 setCurrentVideo(null); // Back to list
             } else {
                 setShowVideosModal(false); // Close modal
             }
        }}>
           {currentVideo ? currentVideo.name : (selectedMediaTitle ? `Videos: ${selectedMediaTitle}` : 'Videos')}
        </ModalHeader>
        <ModalBody className="p-0 bg-dark text-white">
            {loadingVideos ? (
                 <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                 </div>
            ) : currentVideo ? (
                // PLAYER VIEW
                <div className="ratio ratio-16x9">
                    <iframe 
                        src={`https://www.youtube.com/embed/${currentVideo.key}?autoplay=1&rel=0`} 
                        title="YouTube video player" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                    ></iframe>
                </div>
            ) : (
                // LIST VIEW
                <div className="p-3" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {videoList && videoList.length > 0 ? (
                         <div className="d-flex flex-column gap-2">
                            {videoList.map(video => (
                                <div 
                                    key={video.id} 
                                    className="d-flex align-items-center p-2 rounded bg-secondary bg-opacity-25 hover-bg-opacity-50" 
                                    style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                                    onClick={() => setCurrentVideo(video)}
                                >
                                     {/* Thumbnail */}
                                     <div className="flex-shrink-0 position-relative me-3" style={{ width: '120px', aspectRatio: '16/9' }}>
                                         <img 
                                            src={`https://img.youtube.com/vi/${video.key}/mqdefault.jpg`} 
                                            alt={video.name}
                                            className="w-100 h-100 rounded object-fit-cover"
                                         />
                                         <div className="position-absolute top-50 start-50 translate-middle">
                                             <i className="bi bi-play-circle-fill fs-3 text-white border-white bg-dark rounded-circle" style={{ opacity: 0.8 }}></i>
                                         </div>
                                     </div>
                                     
                                     {/* Info */}
                                     <div className="flex-grow-1">
                                         <h6 className="mb-1 text-white">{video.name}</h6>
                                         <span className="badge bg-danger me-2">{video.type}</span>
                                         <small className="text-white-50">{new Date(video.published_at).toLocaleDateString()}</small>
                                     </div>
                                </div>
                            ))}
                         </div>
                    ) : (
                        <div className="text-center py-5 text-white-50">
                            <i className="bi bi-camera-video-off display-4 mb-3 d-block"></i>
                            <p>No s'han trobat videos oficials.</p>
                        </div>
                    )}
                </div>
            )}
        </ModalBody>
      </Modal>

    </MainLayout>
  );
};

export default DashboardPage;
